import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Megaphone,
  Send,
  Loader2,
  Users,
  Smartphone,
  Radio,
  History,
  CheckCircle2,
  AlertTriangle,
  Mail,
  Calendar,
  Repeat,
  Paperclip,
  Eye,
  Edit3,
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Link2,
  Building2,
  Layers,
  FileText,
  Clock,
  Download,
  Filter,
  Play,
  Ban,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const CLASIFICACIONES = [
  "General",
  "Urgente / Importante",
  "Políticas y Normativas",
  "Bienestar y Beneficios",
  "Seguridad y Salud Ocupacional",
  "Capacitación",
  "Celebraciones & Eventos",
];

export const formatFrecuencia = (freq) => {
  if (!freq) return "";
  const f = freq.toLowerCase().trim();
  if (f === "diario" || f === "dia") return "Diario (Cada día)";
  if (f === "semanal" || f === "semana") return "Semanal (Cada semana)";
  if (f === "quincenal" || f === "quincena") return "Quincenal (Cada 15 días)";
  if (f === "mensual" || f === "mes") return "Mensual (Cada mes)";
  if (f === "anual" || f === "año" || f === "anio") return "Anual (Cada año)";
  return freq;
};

export default function ComunicadosPage() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [checkingScheduled, setCheckingScheduled] = useState(false);

  // Form State
  const [titulo, setTitulo] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [contenidoHtml, setContenidoHtml] = useState("");
  const [canal, setCanal] = useState("ambos"); // 'push', 'correo', 'ambos'
  const [clasificacion, setClasificacion] = useState("General");
  const [prioridad, setPrioridad] = useState("normal");
  
  // Destinatarios múltiples
  const [todosDepartamentos, setTodosDepartamentos] = useState(true);
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [todasEmpresas, setTodasEmpresas] = useState(true);
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  // Programación y Recurrencia
  const [esProgramado, setEsProgramado] = useState(false);
  const [fechaProgramada, setFechaProgramada] = useState("");
  const [esRecurrente, setEsRecurrente] = useState(false);
  const [frecuenciaRecurrente, setFrecuenciaRecurrente] = useState("mensual");

  // Archivo Adjunto
  const [archivo, setArchivo] = useState(null);

  // Modo Vista previa en editor
  const [previewMode, setPreviewMode] = useState(false);

  // Ref para insertar tags HTML
  const textareaRef = useRef(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [histRes, logsRes, empRes] = await Promise.all([
        api.getBroadcasts(),
        api.getBroadcastLogs().catch(() => ({ data: [] })),
        api.getEmployees(),
      ]);
      setHistory(histRes.data || []);
      setLogs(logsRes.data || []);
      const emps = empRes.data || [];
      setEmployees(emps);

      const depts = Array.from(new Set(emps.map((e) => e.departamento).filter(Boolean))).sort();
      const comps = Array.from(new Set(emps.map((e) => e.empresa).filter(Boolean))).sort();
      setDepartments(depts);
      setCompanies(comps.length > 0 ? comps : ["CPS Logistics", "CPX"]);
    } catch (err) {
      console.error("Error cargando comunicados:", err);
      toast.error("Error al cargar datos de comunicados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Insertar tags HTML en el contenido enriquecido
  const insertTag = (openTag, closeTag = "") => {
    const el = textareaRef.current;
    if (!el) {
      setContenidoHtml((prev) => prev + openTag + closeTag);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selectedText = el.value.substring(start, end);
    const replacement = openTag + selectedText + closeTag;
    const newText = el.value.substring(0, start) + replacement + el.value.substring(end);
    setContenidoHtml(newText);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + openTag.length, end + openTag.length);
    }, 50);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!titulo.trim() || !mensaje.trim()) {
      toast.error("Por favor completa el título y el mensaje del comunicado");
      return;
    }

    if (esProgramado && !fechaProgramada) {
      toast.error("Indica la fecha y hora programada para el envío");
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("titulo", titulo.trim());
      formData.append("mensaje", mensaje.trim());
      formData.append("contenido_html", contenidoHtml.trim() || `<p>${mensaje.trim()}</p>`);
      formData.append("canal", canal);
      formData.append("clasificacion", clasificacion);
      formData.append("prioridad", prioridad);
      
      const deptsValue = todosDepartamentos ? JSON.stringify(["TODOS"]) : JSON.stringify(selectedDepts);
      const compsValue = todasEmpresas ? JSON.stringify(["TODAS"]) : JSON.stringify(selectedCompanies);
      formData.append("departamentos", deptsValue);
      formData.append("empresas", compsValue);

      formData.append("es_programado", esProgramado ? "true" : "false");
      if (esProgramado && fechaProgramada) {
        formData.append("fecha_programada", fechaProgramada);
      }
      formData.append("es_recurrente", esRecurrente ? "true" : "false");
      if (esRecurrente && frecuenciaRecurrente) {
        formData.append("frecuencia_recurrente", frecuenciaRecurrente);
      }

      if (archivo) {
        formData.append("archivo", archivo);
      }

      const res = await api.sendBroadcast(formData);

      if (esProgramado) {
        toast.success("Comunicado programado exitosamente", {
          description: `Se emitirá en la fecha seleccionada.`,
        });
      } else {
        toast.success("¡Comunicado emitido con éxito!", {
          description: `Notificados ${res.data?.destinatarios_notificados || 0} colaboradores (${res.data?.push_enviados || 0} push, ${res.data?.correos_enviados || 0} correos).`,
        });
      }

      // Reset form
      setTitulo("");
      setMensaje("");
      setContenidoHtml("");
      setArchivo(null);
      setEsProgramado(false);
      setFechaProgramada("");
      setEsRecurrente(false);
      setTodosDepartamentos(true);
      setSelectedDepts([]);
      setTodasEmpresas(true);
      setSelectedCompanies([]);
      loadData();
    } catch (err) {
      console.error("Error enviando comunicado:", err);
      toast.error(err.response?.data?.detail || "Error al emitir el comunicado");
    } finally {
      setSending(false);
    }
  };

  const handleDownloadAdjunto = async (id, filename = "Adjunto.pdf") => {
    try {
      const res = await api.downloadBroadcastAdjunto(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Descarga iniciada");
    } catch (err) {
      toast.error("Error al descargar el archivo adjunto");
    }
  };

  const handleTriggerScheduled = async () => {
    try {
      setCheckingScheduled(true);
      const res = await api.triggerBroadcastsScheduled();
      const despachados = res.data?.despachados || 0;
      if (despachados > 0) {
        toast.success(`¡Procesados ${despachados} comunicados programados!`);
      } else {
        toast.info("No hay comunicados programados pendientes para esta fecha y hora.");
      }
      loadData();
    } catch (err) {
      console.error("Error procesando comunicados programados:", err);
      toast.error("Error al verificar comunicados programados");
    } finally {
      setCheckingScheduled(false);
    }
  };

  const handleSendNow = async (broadcastId, titulo) => {
    if (!window.confirm(`¿Deseas enviar inmediatamente el comunicado "${titulo}" a todos sus destinatarios?`)) {
      return;
    }
    try {
      setLoading(true);
      const res = await api.sendBroadcastNow(broadcastId);
      toast.success(res.data?.message || "Comunicado enviado exitosamente");
      loadData();
    } catch (err) {
      console.error("Error enviando comunicado programado ahora:", err);
      toast.error(err.response?.data?.detail || "Error al forzar envío del comunicado");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSchedule = async (broadcastId, titulo) => {
    if (!window.confirm(`¿Seguro que deseas cancelar la programación del comunicado "${titulo}"? Ya no se enviará automáticamente.`)) {
      return;
    }
    try {
      setLoading(true);
      await api.cancelBroadcastSchedule(broadcastId);
      toast.success("Programación cancelada");
      loadData();
    } catch (err) {
      console.error("Error cancelando programación:", err);
      toast.error(err.response?.data?.detail || "Error al cancelar programación");
    } finally {
      setLoading(false);
    }
  };

  const toggleDept = (dept) => {
    if (selectedDepts.includes(dept)) {
      setSelectedDepts(selectedDepts.filter((d) => d !== dept));
    } else {
      setSelectedDepts([...selectedDepts, dept]);
    }
  };

  const toggleCompany = (comp) => {
    if (selectedCompanies.includes(comp)) {
      setSelectedCompanies(selectedCompanies.filter((c) => c !== comp));
    } else {
      setSelectedCompanies([...selectedCompanies, comp]);
    }
  };

  // Cálculo en tiempo real de audiencia estimada
  const getRecipientEstimate = () => {
    let filtered = employees.filter((e) => e.activo);
    if (!todosDepartamentos && selectedDepts.length > 0) {
      filtered = filtered.filter((e) => selectedDepts.includes(e.departamento));
    }
    if (!todasEmpresas && selectedCompanies.length > 0) {
      filtered = filtered.filter((e) => selectedCompanies.includes(e.empresa));
    }
    return filtered.length;
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-12 animate-fadeIn">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2.5 bg-orange-100 text-orange-600 rounded-2xl shadow-sm">
              <Megaphone className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                Comunicados Institucionales RRHH
              </h1>
              <p className="text-slate-500 text-xs sm:text-sm">
                Emisión multicanal: Notificaciones Push en dispositivos, correos masivos, programación y registro de envíos
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="px-3 py-1.5 bg-white shadow-sm border-slate-200 text-xs font-semibold">
            <Radio className="w-3.5 h-3.5 text-emerald-500 mr-1.5 animate-pulse" />
            Canales Push & Correo Listos
          </Badge>
        </div>
      </div>

      <Tabs defaultValue="nuevo" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="nuevo" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs font-semibold">
            <Send className="w-3.5 h-3.5 mr-1.5 text-orange-500" />
            Nuevo Envío
          </TabsTrigger>
          <TabsTrigger value="historial" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs font-semibold">
            <History className="w-3.5 h-3.5 mr-1.5 text-slate-600" />
            Historial ({history.length})
          </TabsTrigger>
          <TabsTrigger value="logs" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs font-semibold">
            <Clock className="w-3.5 h-3.5 mr-1.5 text-blue-500" />
            Registro de Envíos
          </TabsTrigger>
        </TabsList>

        {/* ================= PESTAÑA 1: NUEVO ENVÍO ================= */}
        <TabsContent value="nuevo" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Formulario Principal */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-slate-200 shadow-sm bg-white">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-800">
                    <Send className="w-5 h-5 text-orange-500" />
                    Redactar Comunicado Oficial
                  </CardTitle>
                  <CardDescription>
                    Completa la información, selecciona los canales de difusión y la audiencia destinataria.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSend} className="space-y-6">
                    {/* Canal de Envío */}
                    <div className="p-4 bg-orange-50/60 rounded-2xl border border-orange-100 space-y-3">
                      <Label className="text-xs font-bold uppercase tracking-wider text-orange-800">
                        Canal de Difusión *
                      </Label>
                      <div className="grid grid-cols-3 gap-2.5">
                        <button
                          type="button"
                          onClick={() => setCanal("ambos")}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            canal === "ambos"
                              ? "bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-500/20"
                              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center gap-1.5 font-bold text-xs">
                            <Radio className="w-3.5 h-3.5" />
                            Push + Correo
                          </div>
                          <p className={`text-[10px] mt-1 ${canal === "ambos" ? "text-orange-100" : "text-slate-400"}`}>
                            Recomendado (Máximo alcance)
                          </p>
                        </button>

                        <button
                          type="button"
                          onClick={() => setCanal("push")}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            canal === "push"
                              ? "bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-500/20"
                              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center gap-1.5 font-bold text-xs">
                            <Smartphone className="w-3.5 h-3.5" />
                            Solo Web Push
                          </div>
                          <p className={`text-[10px] mt-1 ${canal === "push" ? "text-orange-100" : "text-slate-400"}`}>
                            Notificación instantánea en pantalla
                          </p>
                        </button>

                        <button
                          type="button"
                          onClick={() => setCanal("correo")}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            canal === "correo"
                              ? "bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-500/20"
                              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center gap-1.5 font-bold text-xs">
                            <Mail className="w-3.5 h-3.5" />
                            Solo Correo
                          </div>
                          <p className={`text-[10px] mt-1 ${canal === "correo" ? "text-orange-100" : "text-slate-400"}`}>
                            Email corporativo formal
                          </p>
                        </button>
                      </div>
                    </div>

                    {/* Clasificación y Prioridad */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="clasificacion">Clasificación / Categoría</Label>
                        <Select value={clasificacion} onValueChange={setClasificacion}>
                          <SelectTrigger id="clasificacion">
                            <SelectValue placeholder="Selecciona clasificación" />
                          </SelectTrigger>
                          <SelectContent>
                            {CLASIFICACIONES.map((c) => (
                              <SelectItem key={c} value={c}>
                                {c}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="prioridad">Nivel de Prioridad</Label>
                        <Select value={prioridad} onValueChange={setPrioridad}>
                          <SelectTrigger id="prioridad">
                            <SelectValue placeholder="Selecciona prioridad" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="alta">Alta</SelectItem>
                            <SelectItem value="urgente">Urgente</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Título */}
                    <div className="space-y-1.5">
                      <Label htmlFor="titulo">Título del Comunicado *</Label>
                      <Input
                        id="titulo"
                        placeholder="Ej: Actualización de Políticas de Seguro Médico, Asueto del 20 de Octubre..."
                        value={titulo}
                        onChange={(e) => setTitulo(e.target.value)}
                        required
                        className="font-semibold text-slate-800"
                      />
                    </div>

                    {/* Mensaje corto (para Push y resumen) */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="mensaje">Resumen Corto (Notificación Push) *</Label>
                        <span className="text-[11px] text-slate-400">Texto breve que aparece en el móvil</span>
                      </div>
                      <Textarea
                        id="mensaje"
                        rows={2}
                        placeholder="Escribe el mensaje clave que el colaborador leerá de inmediato..."
                        value={mensaje}
                        onChange={(e) => setMensaje(e.target.value)}
                        required
                      />
                    </div>

                    {/* Editor Enriquecido HTML */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-1.5">
                          <Edit3 className="w-4 h-4 text-orange-500" />
                          Contenido Detallado / Editor Enriquecido (HTML)
                        </Label>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant={!previewMode ? "secondary" : "ghost"}
                            onClick={() => setPreviewMode(false)}
                            className="h-7 text-xs px-2.5"
                          >
                            <Edit3 className="w-3.5 h-3.5 mr-1" />
                            Editor
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={previewMode ? "secondary" : "ghost"}
                            onClick={() => setPreviewMode(true)}
                            className="h-7 text-xs px-2.5"
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            Previsualizar
                          </Button>
                        </div>
                      </div>

                      {/* Barra de herramientas de formato rápido */}
                      {!previewMode && (
                        <div className="flex items-center gap-1 p-1.5 bg-slate-100 rounded-lg border border-slate-200 flex-wrap">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-slate-700 hover:bg-white"
                            onClick={() => insertTag("<strong>", "</strong>")}
                            title="Negrita"
                          >
                            <Bold className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-slate-700 hover:bg-white"
                            onClick={() => insertTag("<em>", "</em>")}
                            title="Cursiva"
                          >
                            <Italic className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-slate-700 hover:bg-white"
                            onClick={() => insertTag("<h3 style='color:#ea580c;'>", "</h3>")}
                            title="Título de Sección"
                          >
                            <Heading2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-slate-700 hover:bg-white"
                            onClick={() => insertTag("<ul>\n  <li>", "</li>\n</ul>")}
                            title="Lista con viñetas"
                          >
                            <List className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-slate-700 hover:bg-white"
                            onClick={() => insertTag("<ol>\n  <li>", "</li>\n</ol>")}
                            title="Lista numerada"
                          >
                            <ListOrdered className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-slate-700 hover:bg-white"
                            onClick={() => insertTag("<p style='background:#fff7ed; border-left:4px solid #ea580c; padding:10px; border-radius:4px;'>", "</p>")}
                            title="Cuadro Destacado Naranja"
                          >
                            <Layers className="w-3.5 h-3.5 text-orange-600" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-slate-700 hover:bg-white"
                            onClick={() => {
                              const url = prompt("Ingresa el enlace URL:", "https://");
                              if (url) insertTag(`<a href="${url}" target="_blank" style="color:#ea580c; text-decoration:underline;">`, "</a>");
                            }}
                            title="Insertar Enlace"
                          >
                            <Link2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}

                      {!previewMode ? (
                        <Textarea
                          ref={textareaRef}
                          rows={6}
                          placeholder="<p>Estimados colaboradores,</p><p>Nos complace informarles que...</p>"
                          value={contenidoHtml}
                          onChange={(e) => setContenidoHtml(e.target.value)}
                          className="font-mono text-xs"
                        />
                      ) : (
                        <div className="p-4 bg-white rounded-xl border border-slate-200 min-h-[160px] prose prose-sm max-w-none">
                          <h3 className="text-orange-600 font-bold mb-2">{titulo || "Título del Comunicado"}</h3>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: contenidoHtml || `<p>${mensaje || "Escribe el contenido para previsualizarlo aquí."}</p>`,
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Adjuntar Archivo */}
                    <div className="space-y-1.5">
                      <Label htmlFor="archivo" className="flex items-center gap-1.5">
                        <Paperclip className="w-4 h-4 text-slate-500" />
                        Adjuntar Circular, Infografía o Documento (Opcional)
                      </Label>
                      <Input
                        id="archivo"
                        type="file"
                        onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                        className="cursor-pointer file:cursor-pointer text-xs"
                      />
                      {archivo && (
                        <p className="text-xs text-emerald-600 font-medium">
                          Archivo listo para enviar: {archivo.name} ({Math.round(archivo.size / 1024)} KB)
                        </p>
                      )}
                    </div>

                    {/* Botón Emitir */}
                    <Button
                      type="submit"
                      disabled={sending}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold h-11 shadow-md hover:shadow-lg transition-all"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Despachando Comunicado...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {esProgramado ? "Programar Comunicado" : "Emitir Comunicado Ahora"}
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Columna Derecha: Filtros de Audiencia, Programación y Resumen */}
            <div className="space-y-6">
              {/* Audiencia Estimada */}
              <Card className="border-orange-200 bg-orange-50/40 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-bold text-orange-900 flex items-center gap-2">
                    <Users className="w-4 h-4 text-orange-600" />
                    Audiencia Destinataria Estimada
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-baseline justify-between">
                    <span className="text-4xl font-black text-orange-600">{getRecipientEstimate()}</span>
                    <span className="text-xs text-orange-700 font-semibold">Colaboradores alcanzados</span>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Total activos en la plataforma: {employees.filter((e) => e.activo).length}
                  </p>
                </CardContent>
              </Card>

              {/* Filtro Múltiple de Departamentos */}
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-orange-500" />
                      Departamentos
                    </CardTitle>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] text-slate-500">Todos</span>
                      <Switch
                        checked={todosDepartamentos}
                        onCheckedChange={(val) => {
                          setTodosDepartamentos(val);
                          if (val) setSelectedDepts([]);
                        }}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {!todosDepartamentos ? (
                    <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                      {departments.map((d) => (
                        <label
                          key={d}
                          className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-xs text-slate-700"
                        >
                          <input
                            type="checkbox"
                            checked={selectedDepts.includes(d)}
                            onChange={() => toggleDept(d)}
                            className="rounded text-orange-600 focus:ring-orange-500"
                          />
                          <span className="truncate">{d}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">
                      Se emitirá a colaboradores de todos los departamentos.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Filtro Múltiple de Empresas */}
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-orange-500" />
                      Empresas / Sedes
                    </CardTitle>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] text-slate-500">Todas</span>
                      <Switch
                        checked={todasEmpresas}
                        onCheckedChange={(val) => {
                          setTodasEmpresas(val);
                          if (val) setSelectedCompanies([]);
                        }}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {!todasEmpresas ? (
                    <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                      {companies.map((c) => (
                        <label
                          key={c}
                          className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-xs text-slate-700"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCompanies.includes(c)}
                            onChange={() => toggleCompany(c)}
                            className="rounded text-orange-600 focus:ring-orange-500"
                          />
                          <span className="truncate">{c}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">
                      Se emitirá a todas las empresas y divisiones.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Programación y Recurrencia */}
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-orange-500" />
                    Programación & Recurrencia
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Programado */}
                  <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="space-y-0.5">
                      <Label className="text-xs font-semibold">Envío Programado</Label>
                      <p className="text-[10px] text-slate-500">Emitir en fecha futura</p>
                    </div>
                    <Switch checked={esProgramado} onCheckedChange={setEsProgramado} />
                  </div>

                  {esProgramado && (
                    <div className="space-y-1">
                      <Label htmlFor="fecha_prog" className="text-xs">Fecha y Hora de Emisión</Label>
                      <Input
                        id="fecha_prog"
                        type="datetime-local"
                        value={fechaProgramada}
                        onChange={(e) => setFechaProgramada(e.target.value)}
                        className="text-xs"
                      />
                    </div>
                  )}

                  {/* Recurrente */}
                  <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="space-y-0.5">
                      <Label className="text-xs font-semibold">Comunicado Recurrente</Label>
                      <p className="text-[10px] text-slate-500">Reiterar periódicamente</p>
                    </div>
                    <Switch checked={esRecurrente} onCheckedChange={setEsRecurrente} />
                  </div>

                  {esRecurrente && (
                    <div className="space-y-1">
                      <Label htmlFor="frecuencia" className="text-xs">Frecuencia de Repetición</Label>
                      <Select value={frecuenciaRecurrente} onValueChange={setFrecuenciaRecurrente}>
                        <SelectTrigger id="frecuencia" className="text-xs">
                          <SelectValue placeholder="Selecciona frecuencia" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="diario">Diario (Cada día)</SelectItem>
                          <SelectItem value="semanal">Semanal (Cada semana)</SelectItem>
                          <SelectItem value="quincenal">Quincenal (Cada 15 días)</SelectItem>
                          <SelectItem value="mensual">Mensual (Cada mes)</SelectItem>
                          <SelectItem value="anual">Anual (Cada año)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Resumen dinámico de programación */}
                  {(esProgramado || esRecurrente) && (
                    <div className="bg-amber-50/90 border border-amber-200/90 rounded-xl p-3 text-xs text-amber-900 space-y-1 mt-2">
                      <div className="font-semibold flex items-center gap-1.5 text-amber-800">
                        <Clock className="w-3.5 h-3.5 text-amber-600" />
                        Plan de Emisión Programada:
                      </div>
                      <p className="text-[11px] leading-relaxed text-amber-800">
                        {esProgramado && fechaProgramada
                          ? `📅 Inicio programado: ${new Date(fechaProgramada).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}.`
                          : esProgramado
                          ? "⚠️ Ingresa la fecha y hora futura de salida."
                          : "⚡ El primer envío se despachará de inmediato."}
                      </p>
                      {esRecurrente && (
                        <p className="text-[11px] font-semibold text-purple-700 flex items-center gap-1 mt-1">
                          <Repeat className="w-3 h-3" />
                          Repetición automática: {formatFrecuencia(frecuenciaRecurrente)}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ================= PESTAÑA 2: HISTORIAL DE COMUNICADOS ================= */}
        <TabsContent value="historial" className="space-y-4 mt-6">
          <Card className="border-slate-200 shadow-sm bg-white">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-lg">Historial de Comunicados Emitidos</CardTitle>
                  <CardDescription>
                    Registro de circulares y avisos oficiales publicados por Recursos Humanos
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleTriggerScheduled}
                  disabled={checkingScheduled}
                  className="text-xs font-semibold border-slate-200 hover:bg-orange-50 hover:text-orange-700 hover:border-orange-200 transition-colors self-start sm:self-auto"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${checkingScheduled ? "animate-spin text-orange-600" : "text-slate-500"}`} />
                  Procesar Programados Ahora
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Comunicado</TableHead>
                    <TableHead>Canal & Clasificación</TableHead>
                    <TableHead>Destinatarios</TableHead>
                    <TableHead>Estado & Programación</TableHead>
                    <TableHead>Fecha Emisión / Registro</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="h-32 text-center text-slate-400">
                        No hay comunicados registrados aún.
                      </TableCell>
                    </TableRow>
                  ) : (
                    history.map((h) => {
                      const isProgramado = h.estado_envio === "programado";
                      const isCancelado = h.estado_envio === "cancelado";

                      return (
                        <TableRow key={h.id} className="hover:bg-slate-50/70">
                          <TableCell className="max-w-xs">
                            <div className="font-bold text-slate-800 text-sm">{h.titulo}</div>
                            <div className="text-xs text-slate-500 line-clamp-1">{h.mensaje}</div>
                            <div className="text-[11px] text-slate-400 mt-0.5">Por: {h.creado_por}</div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Badge variant="outline" className="text-[10px] capitalize bg-slate-50 font-semibold">
                                {h.canal || "push"}
                              </Badge>
                              <Badge className="text-[10px] bg-orange-100 text-orange-700 hover:bg-orange-100 border-none font-semibold">
                                {h.clasificacion || "General"}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs font-semibold text-slate-700">
                              {h.total_destinatarios || h.destinatarios || 0} destinatarios
                            </div>
                            <div className="text-[10px] text-slate-400">
                              {h.push_enviados ? `${h.push_enviados} push ` : ""}
                              {h.correos_enviados ? `| ${h.correos_enviados} emails` : ""}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1 items-start">
                              {isProgramado && (
                                <Badge className="text-[10px] bg-blue-100 text-blue-700 hover:bg-blue-100 border-none font-semibold flex items-center gap-1">
                                  <Clock className="w-3 h-3 text-blue-600" />
                                  Prog: {h.fecha_programada || "Pendiente"}
                                </Badge>
                              )}
                              {!isProgramado && !isCancelado && (
                                <Badge className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none font-semibold flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                  Enviado
                                </Badge>
                              )}
                              {isCancelado && (
                                <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 font-semibold">
                                  Cancelado
                                </Badge>
                              )}
                              {h.es_recurrente && (
                                <Badge className="text-[10px] bg-purple-100 text-purple-700 hover:bg-purple-100 border-none font-semibold flex items-center gap-1">
                                  <Repeat className="w-3 h-3 text-purple-600" />
                                  {formatFrecuencia(h.frecuencia_recurrente)}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                            {h.fecha_creacion}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              {isProgramado && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleSendNow(h.id, h.titulo)}
                                    title="Enviar inmediatamente a todos los colaboradores"
                                    className="text-xs h-7 px-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300"
                                  >
                                    <Play className="w-3 h-3 mr-1 fill-emerald-600 text-emerald-600" />
                                    Enviar Ahora
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleCancelSchedule(h.id, h.titulo)}
                                    title="Cancelar esta programación automática"
                                    className="text-xs h-7 px-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                                  >
                                    <Ban className="w-3 h-3 mr-1 text-red-600" />
                                    Cancelar
                                  </Button>
                                </>
                              )}
                              {h.tiene_adjunto && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadAdjunto(h.id, h.archivo_adjunto)}
                                  className="text-xs h-7 border-slate-200"
                                  title="Descargar archivo adjunto"
                                >
                                  <Download className="w-3.5 h-3.5 mr-1 text-slate-600" />
                                  Adjunto
                                </Button>
                              )}
                              {!isProgramado && !h.tiene_adjunto && (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ================= PESTAÑA 3: REGISTRO DE ENVÍOS (LOGS) ================= */}
        <TabsContent value="logs" className="space-y-4 mt-6">
          <Card className="border-slate-200 shadow-sm bg-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-500" />
                    Bitácora Detallada de Envíos
                  </CardTitle>
                  <CardDescription>
                    Auditoría de despachos de notificaciones Push y correos electrónicos
                  </CardDescription>
                </div>
                <Button size="sm" variant="outline" onClick={loadData} className="text-xs">
                  Actualizar Registro
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha / Hora</TableHead>
                    <TableHead>Comunicado</TableHead>
                    <TableHead>Canal</TableHead>
                    <TableHead>Destinatarios</TableHead>
                    <TableHead>Push Enviados</TableHead>
                    <TableHead>Correos Enviados</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-32 text-center text-slate-400">
                        No hay registros de envío todavía.
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} className="hover:bg-slate-50/70 text-xs">
                        <TableCell className="font-mono text-slate-600 whitespace-nowrap">
                          {log.fecha_envio}
                        </TableCell>
                        <TableCell className="font-semibold text-slate-800">
                          {log.titulo}
                        </TableCell>
                        <TableCell className="capitalize">
                          <Badge variant="outline" className="text-[10px]">
                            {log.canales}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-bold text-slate-700">
                          {log.destinatarios_count}
                        </TableCell>
                        <TableCell className="text-emerald-700 font-semibold">
                          {log.push_count}
                        </TableCell>
                        <TableCell className="text-blue-700 font-semibold">
                          {log.correos_count}
                        </TableCell>
                        <TableCell>
                          {log.exitoso ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-none text-[10px]">
                              Exitoso
                            </Badge>
                          ) : (
                            <Badge className="bg-rose-100 text-rose-800 border-none text-[10px]">
                              Fallo Parcial
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
